/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import AbstractModule from "../abstract_module";
import {IGeoLocationInfo, IGeoLocation} from "./scene_geo_location_types";
import IGeoLocationModule from "./scene_geo_location_interface";
import {RenderDocument, Projections} from "js_renderer";
import {vec2, vec3} from "gl-matrix";
import Scene from "../../scene";
import Settings from "../settings";

export default
class GeoLocationModule extends AbstractModule implements IGeoLocationModule {

    protected static _typeName: string = "geoLocation";

    protected _defaultSettings: IGeoLocation;
    protected _settings: IGeoLocation;

    private _EPSG2WKTMap: any;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this._type = GeoLocationModule._typeName;
        this._dependencies = ['lighting'];

        this._defaultSettings = {
            latitude: 46.9479739,
            longitude: 7.447446799999966,
            address: 'Bern, Swiss',
            addressShort: 'Bern, Swiss',
            projectionType: "",
            useLatLng: false
        };

        this._settings = Settings.clone(this._defaultSettings);

        // translate EPSG code to partial wkt
        this._EPSG2WKTMap = {};
        this._EPSG2WKTMap["EPSG:21781"] = "Swiss CH1903 / LV03";
        this._EPSG2WKTMap["EPSG:2056"] = "Swiss CH1903+ / LV95";

        this._EPSG2WKTMap["EPSG:2154"] = "France RGF93 / Lambert-93";
        this._EPSG2WKTMap["EPSG:27561"] = "NTF (Paris) / Lambert Nord France";
        this._EPSG2WKTMap["EPSG:27562"] = "NTF (Paris) / Lambert Centre France";
        this._EPSG2WKTMap["EPSG:27563"] = "NTF (Paris) / Lambert Sud France";
        this._EPSG2WKTMap["EPSG:27564"] = "NTF (Paris) / Lambert Corse";

        this._EPSG2WKTMap["CRS:5243"] = "ETRS89 / LCC Germany (E-N)";
        this._EPSG2WKTMap["EPSG:31466"] = "DHDN / 3-degree Gauss-Kruger zone 2";
        this._EPSG2WKTMap["EPSG:31467"] = "DHDN / 3-degree Gauss-Kruger zone 3";
        this._EPSG2WKTMap["EPSG:31468"] = "DHDN / 3-degree Gauss-Kruger zone 4";
        this._EPSG2WKTMap["EPSG:31469"] = "DHDN / 3-degree Gauss-Kruger zone 5";

        this._EPSG2WKTMap["CRS:5325"] = "ISN2004 / Lambert 2004";
        this._EPSG2WKTMap["EPSG:3038"] = "ETRS89 / TM26";
        this._EPSG2WKTMap["EPSG:3039"] = "ETRS89 / TM27";
        this._EPSG2WKTMap["EPSG:3040"] = "ETRS89 / UTM zone 28N (N-E)";

        this._EPSG2WKTMap["EPSG:26941"] = "NAD83 / California zone 1";
        this._EPSG2WKTMap["EPSG:26942"] = "NAD83 / California zone 2";
        this._EPSG2WKTMap["EPSG:26943"] = "NAD83 / California zone 3";
        this._EPSG2WKTMap["EPSG:26944"] = "NAD83 / California zone 4";
        this._EPSG2WKTMap["EPSG:26945"] = "NAD83 / California zone 5";
        this._EPSG2WKTMap["EPSG:26946"] = "NAD83 / California zone 6";

        this._EPSG2WKTMap["EPSG:4326"] = "WGS 84 (for GPS)";
        this._EPSG2WKTMap["EPSG:32630"] = "WGS 84 / UTM zone 30N";
        this._EPSG2WKTMap["EPSG:32631"] = "WGS 84 / UTM zone 31N";
        this._EPSG2WKTMap["EPSG:32632"] = "WGS 84 / UTM zone 32N";
        this._EPSG2WKTMap["EPSG:32633"] = "WGS 84 / UTM zone 33N";
    }

    public static getClassType(): string {
        return GeoLocationModule._typeName;
    }

    public viewChanged() {
        //
    }

    public viewResized(aContentSize: vec2) {
        //
    }

    public getLatitude(): number {
        return this._settings.latitude;
    }

    public getLongitude(): number {
        return this._settings.longitude;
    }

    public getAddress(): string {
        return this._settings.address;
    }

    public getAddressShort(): string {
        return this._settings.addressShort;
    }

    public getUseLatLng(): boolean {
        return this._settings.useLatLng;
    }

    public getGeoLocationInfo(): IGeoLocationInfo {
        let lat = 0;
        let lng = 0;
        let mainModel = this.sceneAPI.getDocument().getMainModel();
        let zeropoint = mainModel.getZeropointSettingsRef().zeropoint;
        let planetPoint = vec3.fromValues(0, 0, 0);

        if (zeropoint) {
            planetPoint = zeropoint._transform(planetPoint);
        }

        if (this._settings.useLatLng) {
            lat = this._settings.latitude;
            lng = this._settings.longitude;
        } else if (this._settings.projectionType !== "") {
            if (zeropoint) {
                let res = Projections.proj4(this._settings.projectionType, "EPSG:4326", [planetPoint[0], planetPoint[2]]);
                if (res !== undefined) {
                    lng = res[0];
                    lat = res[1];
                }
            }
        }

        return {
            latitude: lat,
            longitude: lng,
            projectionType: {
                EPSG: this._settings.projectionType,
                WKT: this._settings.projectionType !== "" ? this._EPSG2WKTMap[this._settings.projectionType] : ""
            },
            units: this._settings.useLatLng ? ["Lng", "Alt", "Lat"] : ["x", "y", "z"],
            alt: planetPoint[1] // transformed z offset in scene coords
        };
    }

    public setFromModelDescription(aJsonObject: any) {
        let settings: IGeoLocation = {};
/*
        "GeoLocationSettings": {
            "latitude": 0.0000000 ,  "longitude": 0.0000000 , "address": "", "addressShort": "", "projectionType": "", "useLatLng": false
        },
*/
        // backward compatibility
        if (aJsonObject.hasOwnProperty("GeoLocationSettings")) {
            let geoConfig: any = aJsonObject.GeoLocationSettings;

            if (geoConfig.hasOwnProperty('latitude')) {
                settings.latitude = Number(geoConfig.latitude);
            }

            if (geoConfig.hasOwnProperty('longitude')) {
                settings.longitude = Number(geoConfig.longitude);
            }

            if (geoConfig.hasOwnProperty('address')) {
                settings.address = geoConfig.address;
            }

            if (geoConfig.hasOwnProperty('addressShort')) {
                settings.addressShort = geoConfig.addressShort;
            }

            if (geoConfig.hasOwnProperty('projectionType')) {
                settings.projectionType = geoConfig.projectionType;
            }

            if (geoConfig.hasOwnProperty('useLatLng')) {
                settings.useLatLng = geoConfig.useLatLng;
            }
        }

        if (aJsonObject.hasOwnProperty(GeoLocationModule._typeName)) {
            settings = aJsonObject[GeoLocationModule._typeName];
        }
        this.set(settings);
    }

    protected applySettings(aChangedSettings: any, aPreviousSettings: any) {
        if (aChangedSettings) {

            if (aChangedSettings.hasOwnProperty('latitude')
                || aChangedSettings.hasOwnProperty('longitude')
                || aChangedSettings.hasOwnProperty('address')
                || aChangedSettings.hasOwnProperty('addressShort')
                || aChangedSettings.hasOwnProperty('projectionType')
                || aChangedSettings.hasOwnProperty('useLatLng')
            ) {
                // do recompute lighting in dependency
            }
        }
    }

}

Scene.registerSceneAPIModule(GeoLocationModule);
